/**
 * Modal de importación de productos por Excel.
 * Flujo: descargar plantilla → subir fichero → PREVISUALIZAR (dry-run, no escribe)
 * → Importar/Actualizar → resultados por fila. El parseo del .xlsx ocurre en el cliente.
 *
 * Modo "Actualizar existentes": los SKU que ya existen muestran un DIFF campo a
 * campo; el usuario acepta por producto cuáles actualizar. Nunca cambia identidad
 * (SKU, slug, tipo, SKUs de variantes) y fusiona los bloques campo a campo.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, DocumentArrowUpIcon, XMarkIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { metadataApi, productsApi } from '@/services/api';
import { downloadProductTemplate, downloadProductTemplateExtended, parseProductsWorkbook, type ImportProductItem } from '@/utils/productImport';

interface FieldChange { field: string; old: string; new: string }
type RowStatus = 'created' | 'skipped' | 'valid' | 'error' | 'update' | 'updated' | 'nochange';
interface RowResult { index: number; sku?: string; name: string; status: RowStatus; id?: string; error?: string; warning?: string; changes?: FieldChange[] }
interface ImportResponse {
  dryRun: boolean; mode?: 'skip' | 'update'; total: number;
  created: number; updated?: number; skipped: number; failed: number;
  valid: number; willUpdate?: number; results: RowResult[];
}

const STATUS_STYLE: Record<RowStatus, string> = {
  created: 'bg-green-100 text-green-700', valid: 'bg-sky-100 text-sky-700',
  skipped: 'bg-gray-100 text-gray-500', error: 'bg-red-100 text-red-700',
  update: 'bg-indigo-100 text-indigo-700', updated: 'bg-indigo-100 text-indigo-700', nochange: 'bg-gray-100 text-gray-400',
};
const STATUS_LABEL: Record<RowStatus, string> = {
  created: 'Creado', valid: 'Válido', skipped: 'Omitido', error: 'Error',
  update: 'Actualizar', updated: 'Actualizado', nochange: 'Sin cambios',
};

export default function ImportProductsModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [items, setItems] = useState<ImportProductItem[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [parseErrors, setParseErrors] = useState<{ row: number; message: string }[]>([]);
  const [result, setResult] = useState<ImportResponse | null>(null);
  const [committed, setCommitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [updateMode, setUpdateMode] = useState(false);
  const [accepted, setAccepted] = useState<Set<string>>(new Set()); // SKUs (upper) de updates aceptados
  // Árboles propios de la org (VIDA-360): columnas extra en la hoja Categorizacion.
  const [orgTreeIds, setOrgTreeIds] = useState<string[]>([]);
  useEffect(() => {
    metadataApi.getTrees(undefined, true)
      .then((r) => {
        const trees = (Array.isArray(r.data?.data) ? r.data.data : []) as { id: string; organization_id?: string | null }[];
        setOrgTreeIds(trees.filter((t) => t.organization_id).map((t) => t.id));
      })
      .catch(() => setOrgTreeIds([]));
  }, []);

  const runPreview = async (parsed: ImportProductItem[], mode: boolean) => {
    setPreviewError(null);
    try {
      const r = await productsApi.importProducts(parsed as unknown[], true, { mode: mode ? 'update' : 'skip' });
      const res = r.data as ImportResponse;
      setResult(res);
      // Por defecto, aceptar todas las actualizaciones propuestas.
      setAccepted(new Set(res.results.filter((x) => x.status === 'update' && x.sku).map((x) => x.sku!.toUpperCase())));
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setResult(null);
      setPreviewError(status === 404
        ? 'El servidor no tiene el endpoint de importación (backend sin actualizar). Reconstruye el backend y reintenta.'
        : (msg || 'No se pudo validar el fichero en el servidor.'));
    }
  };

  const onFile = async (file: File) => {
    setBusy(true); setResult(null); setCommitted(false); setPreviewError(null);
    try {
      const { items: parsed, rowErrors } = await parseProductsWorkbook(file, orgTreeIds);
      setItems(parsed); setParseErrors(rowErrors); setFileName(file.name);
      if (parsed.length === 0) { toast.error('No se encontraron productos en el fichero'); return; }
      await runPreview(parsed, updateMode);
    } catch (e) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo leer el fichero');
    } finally { setBusy(false); }
  };

  const toggleUpdateMode = async () => {
    const next = !updateMode;
    setUpdateMode(next);
    if (items && items.length) { setBusy(true); await runPreview(items, next); setBusy(false); }
  };

  const doImport = async () => {
    if (!items) return;
    setBusy(true);
    try {
      const r = await productsApi.importProducts(items as unknown[], false, {
        mode: updateMode ? 'update' : 'skip',
        updateSkus: updateMode ? [...accepted] : undefined,
      });
      const res = r.data as ImportResponse;
      setResult(res); setCommitted(true);
      toast.success(`${res.created} creados${res.updated ? ` · ${res.updated} actualizados` : ''} · ${res.skipped} omitidos${res.failed ? ` · ${res.failed} error` : ''}`);
      if ((res.created || 0) + (res.updated || 0) > 0) onImported();
    } catch (e) {
      toast.error((e as { response?: { data?: { error?: string } } })?.response?.data?.error || 'No se pudo importar');
    } finally { setBusy(false); }
  };

  const statusByIndex = new Map<number, RowResult>();
  if (result) for (const r of result.results) statusByIndex.set(r.index, r);
  const acceptedCount = updateMode ? [...accepted].length : 0;
  const toApply = (result?.valid || 0) + acceptedCount;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-lg w-full max-w-3xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importar productos desde Excel</h2>
            <p className="text-sm text-gray-500">Descarga una plantilla (simple para lo habitual, extendida para todos los campos), rellénala y súbela. Verás una previsualización antes de crear nada.</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700"><XMarkIcon className="w-6 h-6" /></button>
        </div>

        {/* Paso 1 · plantilla */}
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <button onClick={() => void downloadProductTemplate()} title="Campos más usados, una hoja" className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
            <ArrowDownTrayIcon className="w-5 h-5" /> Plantilla simple
          </button>
          <button onClick={() => void downloadProductTemplateExtended(orgTreeIds)} title="Todos los campos, multi-hoja" className="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
            <ArrowDownTrayIcon className="w-5 h-5" /> Plantilla extendida
          </button>
          <label className="inline-flex items-center gap-2 px-3 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 cursor-pointer">
            <DocumentArrowUpIcon className="w-5 h-5" /> Subir fichero relleno
            <input type="file" accept=".xlsx,.xls" className="hidden" disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void onFile(f); e.target.value = ''; }} />
          </label>
          {fileName && <span className="text-xs text-gray-500 truncate">{fileName}</span>}
          {busy && <span className="text-xs text-gray-400">Procesando…</span>}
        </div>

        {/* Modo actualizar */}
        {!committed && (
          <label className="flex items-center gap-2 mb-4 text-sm text-gray-700 select-none">
            <input type="checkbox" checked={updateMode} onChange={() => void toggleUpdateMode()} disabled={busy}
              className="rounded border-gray-300 text-vida-600 focus:ring-vida-500" />
            Actualizar productos que ya existen (por SKU)
            <span className="text-xs text-gray-400">— nunca cambia SKU, slug, tipo ni SKUs de variantes; verás el detalle y aceptas por producto</span>
          </label>
        )}

        {previewError && (
          <div className="mb-3 rounded-lg bg-red-50 border border-red-200 p-2.5 text-xs text-red-700">
            <p className="font-medium flex items-center gap-1"><ExclamationTriangleIcon className="w-4 h-4" /> {previewError}</p>
          </div>
        )}

        {parseErrors.length > 0 && (
          <div className="mb-3 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800">
            <p className="font-medium flex items-center gap-1"><ExclamationTriangleIcon className="w-4 h-4" /> Filas ignoradas al leer el fichero:</p>
            <ul className="mt-1 list-disc list-inside">
              {parseErrors.slice(0, 8).map((e, i) => <li key={i}>Fila {e.row}: {e.message}</li>)}
              {parseErrors.length > 8 && <li>… y {parseErrors.length - 8} más</li>}
            </ul>
          </div>
        )}

        {/* Resumen del dry-run / commit */}
        {result && (
          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            {committed
              ? <>
                  <Badge className="bg-green-100 text-green-700">{result.created} creados</Badge>
                  {(result.updated || 0) > 0 && <Badge className="bg-indigo-100 text-indigo-700">{result.updated} actualizados</Badge>}
                  <Badge className="bg-gray-100 text-gray-600">{result.skipped} omitidos</Badge>
                  {result.failed > 0 && <Badge className="bg-red-100 text-red-700">{result.failed} con error</Badge>}
                </>
              : <>
                  <Badge className="bg-sky-100 text-sky-700">{result.valid} para crear</Badge>
                  {updateMode && (result.willUpdate || 0) > 0 && <Badge className="bg-indigo-100 text-indigo-700">{acceptedCount}/{result.willUpdate} para actualizar</Badge>}
                  <Badge className="bg-gray-100 text-gray-600">{result.skipped} sin cambios/omitidos</Badge>
                  {result.failed > 0 && <Badge className="bg-red-100 text-red-700">{result.failed} con error</Badge>}
                </>}
          </div>
        )}

        {/* Tabla de previsualización */}
        {items && items.length > 0 && (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs text-gray-500">
                  <th className="py-2 px-3">#</th>
                  <th className="py-2 px-3">Nombre</th>
                  <th className="py-2 px-3">SKU</th>
                  <th className="py-2 px-3">Tipo</th>
                  <th className="py-2 px-3">Estado</th>
                  <th className="py-2 px-3">Cambios</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => {
                  const st = statusByIndex.get(i);
                  const skuU = it.sku?.toUpperCase();
                  const isUpdate = st?.status === 'update';
                  return (
                    <tr key={i} className="border-t border-gray-100 align-top">
                      <td className="py-1.5 px-3 text-gray-400">{i + 1}</td>
                      <td className="py-1.5 px-3 text-gray-800">{it.name}</td>
                      <td className="py-1.5 px-3 text-gray-500">{it.sku || '—'}</td>
                      <td className="py-1.5 px-3 text-gray-500">{it.type === 'composite' ? 'Compuesto' : it.type === 'collaborative' ? 'Colaborativo' : 'Simple'}</td>
                      <td className="py-1.5 px-3">
                        {st ? (
                          <span className="inline-flex items-center gap-1.5">
                            {isUpdate && !committed && skuU && (
                              <input type="checkbox" checked={accepted.has(skuU)}
                                onChange={(e) => setAccepted((prev) => { const n = new Set(prev); if (e.target.checked) n.add(skuU); else n.delete(skuU); return n; })}
                                className="rounded border-gray-300 text-vida-600 focus:ring-vida-500" title="Aceptar esta actualización" />
                            )}
                            <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${STATUS_STYLE[st.status]}`} title={st.error || ''}>
                              {STATUS_LABEL[st.status]}{st.error ? ' · ' + st.error : ''}
                            </span>
                          </span>
                        ) : <span className="text-gray-300">—</span>}
                        {st?.warning && <div className="text-[10px] text-amber-600 mt-0.5" title={st.warning}>⚠ {st.warning}</div>}
                      </td>
                      <td className="py-1.5 px-3">
                        {st?.changes?.length ? (
                          <ul className="text-[11px] text-gray-600 space-y-0.5">
                            {st.changes.slice(0, 6).map((c, k) => (
                              <li key={k}><span className="text-gray-400">{c.field}:</span> <span className="line-through text-gray-400">{c.old}</span> → <span className="text-gray-800">{c.new}</span></li>
                            ))}
                            {st.changes.length > 6 && <li className="text-gray-400">… y {st.changes.length - 6} más</li>}
                          </ul>
                        ) : <span className="text-gray-300 text-[11px]">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {result && !committed && toApply === 0 && (
          <p className="mt-3 text-xs text-gray-500">
            {result.skipped > 0 && result.failed === 0
              ? (updateMode
                  ? 'No hay nada que aplicar: los SKU existentes no traen cambios y no hay productos nuevos.'
                  : 'No hay nada nuevo que importar: todos los SKU del fichero ya existen. Activa «Actualizar existentes» para modificarlos.')
              : 'Ninguna fila lista. Revisa la columna «Estado» para ver el motivo de cada fila.'}
          </p>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">{committed ? 'Cerrar' : 'Cancelar'}</button>
          {!committed && (
            <button onClick={() => void doImport()} disabled={busy || !result || toApply === 0}
              className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white rounded-lg text-sm font-medium hover:bg-vida-700 disabled:opacity-50">
              <CheckCircleIcon className="w-5 h-5" /> {updateMode ? 'Importar / Actualizar' : 'Importar'} {toApply}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Badge({ children, className }: { children: React.ReactNode; className: string }) {
  return <span className={`px-2 py-0.5 rounded-full font-medium ${className}`}>{children}</span>;
}
